import { useContext, useEffect, useState } from "react"
import BaseModal from "../../../../components/layout/BaseModal"
import OfferModalScreen1 from "./ModalOfferScreen1"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import Loading from "../../../../components/common/Loading"
import ErrorPage from "../../../404/ErrorPage"
import { getCustomerNamesOnly, getSingleCustomerData } from "../../../Kunden/api/apiCalls"
import { useAddOfferMutation } from "../../api/services"
import { ModalContext } from "../../../../components/contexts/ModalContext"
import { OfferPost } from "../../../../data/Types"
import { useDelayWhenSearchWithPagination } from "../../../../services/functions"
import { Form, FormInstance } from "antd"
import AddBillOrOfferModalScreen2 from "./AddBillOrOfferModalScreen2"
import AddBillOrOfferModalScreen3 from "./AddBillOrOfferModalScreen3"
import AddBillOrOfferModalScreen4 from "./AddBillOrOfferModalScreen4"
import { useLocation } from "react-router-dom"
import { GeneralQueryKeys } from "../../../../api/constants"
import { P } from "../../../../auth/permissions"
import { Authorized } from "../../../../components/authentication/Authorized"

type AddOfferModalType = {
    queryKey: string
}

export default function AddOfferModal(props: AddOfferModalType) {
    const [newOffer, setnewOffer] = useState<OfferPost>()
    const [searchTerm, setsearchTerm] = useState("")
    const searchParams = useDelayWhenSearchWithPagination(searchTerm)

    // GET all customer names for search
    const queryKeyAllCustomer = GeneralQueryKeys.CUSTOMER_NAMES_ONLY + searchParams
    const {
        data: customerData,
        error: errorCustomer,
        isLoading: isLoadingCustomer,
    } = useQuery({
        queryKey: [queryKeyAllCustomer],
        queryFn: () => getCustomerNamesOnly(searchParams),
    })

    // GET single customer only with name & objects
    const queryKeySingleCustomer = GeneralQueryKeys.CUSTOMER_SINGLE + newOffer?.customer_id
    const {
        data: singleCustomer,
        error: errorSingleCustomer,
        isLoading: isLoadingSingleCustomer,
    } = useQuery({
        queryKey: [queryKeySingleCustomer],
        queryFn: () =>
            getSingleCustomerData(
                newOffer?.customer_id ?? "",
                "customer.name",
                "-nr,-objectInfo,-customer_id,-employee_ids,-mailReports,-s3Documents,-serviceContract,-archived,-ranking,-deleted,-costCenter,-zipExportDownloadedBy"
            ),
        enabled: !!newOffer?.customer_id,
    })

    // ADD OFFER
    const [sendLV, setsendLV] = useState(false)
    const [documents, setdocuments] = useState<any[]>()
    const queryClient = useQueryClient()
    const modalCon = useContext(ModalContext)
    const { mutate: addOffer } = useAddOfferMutation(queryClient, modalCon, props.queryKey)
    const handleNewOffer = () => {
        modalCon?.trigger(0)
        const formData = new FormData()
        formData.append("offer", JSON.stringify(newOffer))
        documents?.forEach((doc: any) => {
            if (doc) {
                formData.append("additionalDocuments", doc)
            }
        })
        addOffer({ body: formData, leistungsVerzeichnis: sendLV })
        clearFormAndModal()
    }

    // FE LOGIC
    const [screen, setscreen] = useState<number>(1)
    const [formScreen1] = Form.useForm()
    const [formScreen2] = Form.useForm()
    const clearFormAndModal = () => {
        formScreen1.resetFields()
        formScreen2.resetFields()
        setdocuments(undefined)
        setnewOffer(undefined)
        setsendLV(false)
        setscreen(1)
    }
    const handleNextModalScreen = async (screen: number, form?: FormInstance) => {
        try {
            if (form) await form.validateFields()
            setscreen(screen)
        } catch {
            console.log("Correction")
        }
    }

    // IN CASE OBJECT-ID / CUSTOMER-ID HAVE BEEN PASSED THROUGH THE URL
    const location = useLocation()
    const queryParams = new URLSearchParams(location.search)
    useEffect(() => {
        if (queryParams.get("oid") && !newOffer?.object_id) {
            const oid = queryParams.get("oid")
            const adress = queryParams.get("adress")
            queryParams.delete("oid")
            queryParams.delete("adress")
            setnewOffer((prev: any) => ({
                ...prev,
                object_id: oid,
            }))
            formScreen1.setFieldsValue({ object_id: adress })
        }
        if (queryParams.get("cid") && !newOffer?.customer_id) {
            const cid = queryParams.get("cid")
            const customer = queryParams.get("customer")
            queryParams.delete("cid")
            queryParams.delete("customer")
            setnewOffer((prev: any) => ({
                ...prev,
                customer_id: cid,
            }))
            formScreen1.setFieldsValue({ customer_id: customer })
        }
    }, [])

    return (
        <Authorized anyOf={[P.buchhaltung_read_write, P.buchhaltung_read_write_restricted]}>
            <BaseModal
                title={"Neues Angebot erstellen"}
                modalId={1}
                bottomRow={
                    <div className="flex justify-between items-center">
                        <button
                            disabled={screen === 1}
                            className={`underline text-sm ${screen === 1 && "cursor-not-allowed text-gray-300"}`}
                            onClick={() => setscreen(screen - 1)}>
                            zurück
                        </button>
                        {screen === 4 ? (
                            <button className={`btn btn-primary`} onClick={handleNewOffer}>
                                Abschicken
                            </button>
                        ) : screen === 3 ? (
                            <button className="btn btn-primary btn-outline" onClick={() => handleNextModalScreen(4)}>
                                Weiter
                            </button>
                        ) : screen === 2 ? (
                            <button className="btn btn-primary btn-outline" onClick={() => handleNextModalScreen(3, formScreen2)}>
                                Weiter
                            </button>
                        ) : (
                            <button className="btn btn-primary btn-outline" onClick={() => handleNextModalScreen(2, formScreen1)}>
                                Weiter
                            </button>
                        )}
                    </div>
                }>
                <div className="overflow-hidden relative h-full w-full">
                    {isLoadingSingleCustomer && <Loading />}
                    {errorSingleCustomer && <ErrorPage />}
                    {!errorSingleCustomer && (
                        <>
                            <OfferModalScreen1
                                visible={true}
                                form={formScreen1}
                                doesLValreadyExist={
                                    singleCustomer?.objects?.find((item: { _id: string | undefined }) => item._id === newOffer?.object_id)
                                        ?.listOfServices
                                }
                                setsendLV={() => setsendLV(!sendLV)}
                                newOffer={newOffer as OfferPost}
                                setnewOffer={setnewOffer}
                                objectArray={singleCustomer?.objects}
                                customerArray={customerData}
                                searchTerm={searchTerm}
                                setsearchTerm={setsearchTerm}
                                isLoadingCustomer={isLoadingCustomer}
                                errorCustomer={errorCustomer}
                            />
                            <AddBillOrOfferModalScreen2
                                typeOffer
                                positionsRequiredOption
                                form={formScreen2}
                                visible={screen >= 2}
                                newBillOrOffer={newOffer as OfferPost}
                                setnewBillOrOffer={setnewOffer}
                            />
                            <AddBillOrOfferModalScreen3 visible={screen >= 3} documents={documents ?? []} setdocuments={setdocuments} />
                            <AddBillOrOfferModalScreen4
                                visible={screen >= 4}
                                newBillOrOffer={newOffer as OfferPost}
                                customer={singleCustomer}
                                documents={documents}
                            />
                        </>
                    )}
                </div>
            </BaseModal>
        </Authorized>
    )
}
